import axios from "axios";
import moment from "moment";
import { useState, useCallback } from "react";
import { Timestamp } from "firebase/firestore";
import { debounceByKey } from "./debounceByKey";
import { Installation, InstallationStep } from "types/Installation";

export const LOCK_INSTRUCTION = 'Press the remote to lock the device';
export const LOCK_CONFIRMATION = 'Device Locked';

export const UNLOCK_INSTRUCTION = 'Press the remote to unlock the device';
export const UNLOCK_CONFIRMATION = 'Device Unlocked';

export const IGNITION_ON_INSTRUCTION = 'Turn on the ignition';
export const IGNITION_ON_CONFIRMATION = 'Ignition On';

export const IGNITION_OFF_INSTRUCTION = 'Turn off the ignition';
export const IGNITION_OFF_CONFIRMATION = 'Ignition Off';

export const BATTERY_CONNECTION_INSTRUCTION = 'Connect the vehicle battery';
export const BATTERY_CONNECTION_CONFIRMATION = 'Vehicle Battery Connected';

export const BATTERY_DISCONNECTION_INSTRUCTION = 'Disconnect the vehicle battery';
export const BATTERY_DISCONNECTION_CONFIRMATION = 'Vehicle Battery Disconnected';

export const WIRE_TAMPER_ACTIVATION_INSTRUCTION = 'Activate wire tamper';
export const WIRE_TAMPER_ACTIVATION_CONFIRMATION = 'Wire Tamper Activated';

export const WIRE_TAMPER_DEACTIVATION_INSTRUCTION = 'Deactivate wire tamper';
export const WIRE_TAMPER_DEACTIVATION_CONFIRMATION = 'Wire Tamper Deactivated';

export const MOVEMENT_TAMPER_INSTRUCTION = 'Tap the device a few times';
export const MOVEMENT_TAMPER_CONFIRMATION = 'Tamper Confirmed';

export const REMOTE_PANIC_INSTRUCTION = 'Press the remote many times quickly until it flashes red light';
export const REMOTE_PANIC_CONFIRMATION = 'Panic confirmed';

export const PANIC_BUTTON_INSTRUCTION = 'Press the panic button';
export const PANIC_BUTTON_CONFIRMATION = 'Panic confirmed';

export const FUEL_CUT_INSTRUCTION = 'Wait for fuel-cut';
export const FUEL_CUT_CONFIRMATION = 'Fuel-Cut performed';

export const AUTOLOCK_ENABLE_INSTRUCTION = 'Wait for Auto-Lock to be enabled';
export const AUTOLOCK_ENABLE_CONFIRMATION = 'Auto-Lock Enabled';

export const AUTOLOCK_DISABLE_INSTRUCTION = 'Wait for Auto-Lock to be disabled';
export const AUTOLOCK_DISABLE_CONFIRMATION = 'Auto-Lock Disabled';

export const IGNITION_LOCK_INSTRUCTION = LOCK_INSTRUCTION;
export const IGNITION_LOCK_CONFIRMATION = 'Ignition-Lock Confirmed';

export const STARTER_LOCK_STATE_INSTRUCTION = 'Try starting the engine while the device is locked';
export const STARTER_LOCK_STATE_CONFIRMATION = 'State Confirmed';
export const STARTER_LOCK_USER_CONFIRMATION = 'User Confirmed';

export const ENGINE_ON_INSTRUCTION = 'Start the engine';
export const ENGINE_ON_CONFIRMATION = 'Engine Started';

export const ADMIN_UNLOCK_INSTRUCTION = 'Wait for Admin-Unlock';
export const ADMIN_UNLOCK_CONFIRMATION = 'Admin Unlocked';

export const YES_ENGINE_STOPPED = 'YES-ENGINE-STOPPED';
export const NO_ENGINE_NOT_STOPPED = 'NO-ENGINE-NOT-STOPPED';

export const DEVICE_AUTOLOCKED_INSTRUCTION = 'Wait for device to Auto-Lock';
export const DEVICE_AUTOLOCKED_CONFIRMATION = 'Device Auto Locked';

export enum SystemState {
  // Unit is unlocked and vehicle can be driven.
  SYSTEM_STATE_UNLOCKED = 0,
  // Unit is locked and vehicle may not be driven.
  SYSTEM_STATE_LOCKED = 1,
  // Unit is admin-locked. It will continue driving but will not start once stopped and turned off.
  SYSTEM_STATE_ADMIN_LOCKED = 2,
  // Unit is locked down. Fuel is cut until the vehicle stops, then engine lock is engaged (if fitted).
  SYSTEM_STATE_HIJACK_LOCKED = 3,
  // Undefined system state. Should never occur.
  SYSTEM_STATE_UNKNOWN = 255,
}

export enum TamperType {
  // Power to the unit has been disconnected.
  TAMPER_TYPE_BATTERY = 0,
  // Jamming of the LTE signal has been detected.
  TAMPER_TYPE_LTE_JAM = 1,
  // Jamming of the GPS signal has been detected.
  TAMPER_TYPE_GPS_JAM = 2,
  // Movement has been detected while the unit is locked.
  TAMPER_TYPE_MOVEMENT = 3,
  // Vehicle battery has been disconnected.
  TAMPER_TYPE_BATTERY_DISCONNECT = 4,
  // Tamper wire has been triggered
  TAMPER_TYPE_WIRE = 5,
  // Other unknown tamper.
  TAMPER_TYPE_UNKNOWN = 255,
}

export enum LockType {
  // Lock type used by normal user (e.g. with remote).
  LOCK_TYPE_NORMAL = 0,
  // Lock type for fleet use.
  LOCK_TYPE_ADMIN = 1,
  // Lock type to lock vehicle down in event of hijacking.
  LOCK_TYPE_HIJACK = 2,
  // Lock type to indicate the type of lock is unknown.
  LOCK_TYPE_UNKNOWN = 255,
}

export enum LockCommandSource {
  LOCK_COMMAND_SOURCE_UNSPECIFIED = 0,
  // Command originated from a remote.
  LOCK_COMMAND_SOURCE_REMOTE = 1,
  // Command originated from the mobile app or backend.
  LOCK_COMMAND_SOURCE_APP = 2,
  // Command originated from an auto-lock operation.
  LOCK_COMMAND_SOURCE_AUTO_LOCK = 3,
}

export function isNullOrUndefined(value: any): boolean {
  return value === null || value === undefined;
}

export function isNullOrUndefinedOrWhitespace(value: any): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0);
}

export function isValue(value: any): boolean {
  return isNullOrUndefinedOrWhitespace(value) === false;
}

export function getLockTypeString(lockTypeNumber: number | null | undefined) {
  switch (lockTypeNumber) {
    case LockType.LOCK_TYPE_ADMIN : return 'Admin';
    case LockType.LOCK_TYPE_HIJACK : return 'Hijack';
    case LockType.LOCK_TYPE_NORMAL : return 'Normal';
    case LockType.LOCK_TYPE_UNKNOWN : return 'Unknown';
  }
}

export function getLockSourceString(lockSourceNumber: number | null | undefined) {
  switch (lockSourceNumber) {
    case LockCommandSource.LOCK_COMMAND_SOURCE_APP : return 'App';
    case LockCommandSource.LOCK_COMMAND_SOURCE_REMOTE : return 'Remote';
    case LockCommandSource.LOCK_COMMAND_SOURCE_AUTO_LOCK : return 'Auto-Lock';
    case LockCommandSource.LOCK_COMMAND_SOURCE_UNSPECIFIED : return 'Unspecified';
  }
}

export function isLocked(device: any, testStartTime: Timestamp | null = null, viaRemote: boolean | null = null) {

  const logs = [
    { type: "LockResultLog", log: device?.LockResultLog?.success ? device?.LockResultLog : { logTimestamp: 0 } }, // we trust this absolutely on this matter (as long as it's successful)
    viaRemote ? { log: { logTimestamp: 0 } } : { type: "StatusUpdateLog", log: isValue(device?.StatusUpdateLog?.currentState) ? device?.StatusUpdateLog : { logTimestamp: 0 } }, // we trust this if it actually has a value for currentState
  ];
  
  const latest = logs.reduce((max, current) => 
    current.log.logTimestamp > max.log.logTimestamp ? current : max
  );

  if (testStartTime && latest.log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return;
  }

  switch (latest?.type) {
    case "LockResultLog": return viaRemote ? (latest.log.requestLock && latest.log.source === LockCommandSource.LOCK_COMMAND_SOURCE_REMOTE) : latest.log.requestLock;
    case "StatusUpdateLog": return [SystemState.SYSTEM_STATE_LOCKED, SystemState.SYSTEM_STATE_ADMIN_LOCKED, SystemState.SYSTEM_STATE_HIJACK_LOCKED].includes(latest.log.currentState);
  }
}

export function isUnlocked(device: any, testStartTime: Timestamp | null = null, viaRemote: boolean | null = null) {
  return isLocked(device, testStartTime, viaRemote) !== true;
}

export function ignitionIsOn(device: any) {

  const logs = [
    { type: "IgnitionUpdateLog", log: device?.IgnitionUpdateLog ?? { logTimestamp: 0 } }, // we trust this absolutely in this matter
  ];
  
  const latest = logs.reduce((max, current) => current.log.logTimestamp > max.log.logTimestamp ? current : max);

  switch (latest?.type) {
    case "IgnitionUpdateLog": return latest.log.ignitionOn;
  }
}

export function ignitionIsOff(device: any) {
  return ignitionIsOn(device) === false;
}

export function getLocationUpdateLog(device: any, testStartTime: Timestamp | null) {

  const log = device?.LocationUpdateLog;

  if (!log) {
    return null;
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return null;
  }

  return log;
}

export function getConfigurationUpdateLog(device: any) {
  return device?.ConfigurationStatusLog;
}

export function getAutoLockDelaySeconds(device: any) {
  
  const logs = [
    { type: "StatusUpdateLog", log: device?.StatusUpdateLog && isValue(device?.StatusUpdateLog?.autoLockPeriodSec) ? device?.StatusUpdateLog : { logTimestamp: 0 } }, // we trust this if it actually has a value for autoLockPeriodSec
    { type: "ConfigurationStatusLog", log: device?.ConfigurationStatusLog && isValue(device?.ConfigurationStatusLog?.configuration?.AutoLockConfiguration?.AutoLockDelaySeconds) ? device?.ConfigurationStatusLog : { logTimestamp: 0 } }, // we trust this if it actually has a value for AutoLockDelaySeconds
  ];
  
  const latest = logs.reduce((max, current) => current.log.logTimestamp > max.log.logTimestamp ? current : max);

  switch (latest?.type) {
    case "StatusUpdateLog": return latest.log.autoLockPeriodSec;
    case "ConfigurationStatusLog": return latest.log.configuration?.AutoLockConfiguration?.AutoLockDelaySeconds;
  }
}

export function autoLockIsOn(device: any) {
  const autoLockDelaySeconds = getAutoLockDelaySeconds(device);
  return isValue(autoLockDelaySeconds) && autoLockDelaySeconds > 0;
}

export function autoLockIsOff(device: any) {
  return autoLockIsOn(device) === false;
}

export function getLocationLogValidationErrors(locationUpdateLog: any) {
    
  const errors: string[] = [];
  
  if (!locationUpdateLog) {
    return errors;
  }
  
  if (!locationUpdateLog?.location) {
    errors.push('.location is missing');
  }

  if (!locationUpdateLog?.location?.latLng) {
    errors.push('.location.latLng is missing');
  }
  
  if (!locationUpdateLog?.location?.latLng?.latitudeDeg) {
    errors.push('.location.latLng.latitudeDeg is missing');
  }

  if (!locationUpdateLog?.location?.latLng?.longitudeDeg) {
    errors.push('.location.latLng.longitudeDeg is missing');
  }

  return errors;
}

export function ignitionIsOnWhileLocked(device: any, testStartTime: Timestamp | null) {

  const log = device?.IgnitionUpdateLog;

  if (!log) {
    return false;
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }

  return log?.ignitionOn && log?.locked;
}

export function vehicleBatteryIsConnected(device: any) {

  const logs = [
    { type: "BatteryStatusLog", log: device?.BatteryStatusLog ?? { logTimestamp: 0 } }, // we trust this absolutely on this matter
  ];
  
  const latest = logs.reduce((max, current) => current.log.logTimestamp > max.log.logTimestamp ? current : max);

  switch (latest?.type) {
    case "BatteryStatusLog": return latest.log.vehicleBatteryConnected;
  }
}

export function vehicleBatteryIsDisconnected(device: any) {
  return vehicleBatteryIsConnected(device) === false;
}

export function getPanicRemoteSerialNumbers(device: any) {
  return device.ConfigurationStatusLog?.configuration?.RemotesConfiguration?.RemoteConfigurations?.filter((x: any) => x?.IsPanic)?.map((x: any) => x?.RemoteSerialNumber?.trim())?.filter((x: any) => x?.trim()) || [];
}

export function getNormalRemoteSerialNumbers(device: any) {
  return device.ConfigurationStatusLog?.configuration?.RemotesConfiguration?.RemoteConfigurations?.filter((x: any) => !x?.IsPanic)?.map((x: any) => x?.RemoteSerialNumber?.trim())?.filter((x: any) => x?.trim()) || [];
}

export function panicButtonTriggered(device: any, testStartTime: Timestamp | null) {

  const log = device?.PanicLog;

  if (!log) {
    return false; // no log
  }

  const panicRemoteSerialNumbers = getPanicRemoteSerialNumbers(device);

  if (!panicRemoteSerialNumbers.includes(log.sourceRemote?.serialNumber)) {
    return false; // not a panic remote 
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }

  return true;
}

export function remotePanicTriggered(device: any, testStartTime: Timestamp | null) {

  const log = device?.PanicLog;

  if (!log) {
    return false; // no log
  }

  const normalRemoteSerialNumbers = getNormalRemoteSerialNumbers(device);

  if (!normalRemoteSerialNumbers.includes(log.sourceRemote?.serialNumber)) {
    return false; // not normal remote
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }

  return true;
}

export function lockRequestIsUnsuccessfulWhileIgnitionIsOn(device: any, testStartTime: Timestamp | null) {

  const log = device?.LockResultLog;

  if (!log) {
    return false; // no log
  }

  if (!log.requestLock) {
    return false; // not a lock request
  }

  if (log.success) {
    return false; // not unsuccessful 
  }

  if (!log.ignitionOn) {
    return false; // ignition is off 
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }
  
  return true;
}

export function movementTamperIsActive(device: any, testStartTime: Timestamp | null) {

  const log = device?.TamperLog;

  if (!log) {
    return false; // no log
  }

  if (log.tamperType !== TamperType.TAMPER_TYPE_MOVEMENT) {
    return false; // not movement
  }

  if (!log.active) {
    return false; // not active 
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }
  
  return true;
}

export function wireTamperIsActivated(device: any, testStartTime: Timestamp | null = null) {

  const log = device?.WireTamperLog;

  if (!log) {
    return false; // no log
  }

  if (!log.active) {
    return false; // not active 
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }
  
  return true;
}

export function wireTamperIsDeactivated(device: any, testStartTime: Timestamp | null) {
  return wireTamperIsActivated(device, testStartTime) !== true;
}

export function fuelIsCut(device: any, testStartTime: Timestamp | null) {

  const log = device?.LockResultLog;

  if (!log) {
    return false; // no log
  }

  if (!log.success) {
    return false; // not successful
  }

  // 30 Jan 2025 Request from MF : https://teams.microsoft.com/l/message/19:57f12697-f80d-49dc-9315-ec494525453b_dc66071c-db7c-43ab-a3f5-255b30c9ec4c@unq.gbl.spaces/1769672005898?context=%7B%22contextType%22%3A%22chat%22%7D
  //if (!log.ignitionOn) {
  //  return false; // ignition not on
  //}

  if (!log.requestLock) {
    return false; // not lock request
  }

  if (log.requestLockType !== LockType.LOCK_TYPE_HIJACK) {
    return false; // not active 
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }
  
  return true;
}

export function deviceAutoLocked(device: any, testStartTime: Timestamp | null) {

  const log = device?.LockResultLog;

  if (!log) {
    return false; // no log
  }

  if (!log.success) {
    return false; // not successful
  }

  if (!log.requestLock) {
    return false; // not lock request
  }

  if (log.source !== LockCommandSource.LOCK_COMMAND_SOURCE_AUTO_LOCK) {
    return false; // not active 
  }

  if (testStartTime && log.logTimestamp < getDateFromTimestamp(testStartTime).getTime()) {
    return false;
  }
  
  return true;
}

export function getDateFromTimestamp(timestamp: any) {
  
  if (timestamp) {
    if (typeof(timestamp?.toDate) === 'function') {
      return timestamp.toDate();
    } else if (timestamp?.seconds) {
      return moment.unix(timestamp.seconds).toDate()
    } else if (typeof(timestamp) === 'number' || typeof(timestamp) === 'string') {
      return moment(timestamp).toDate();
    }
  }
  
}

export function getDisplayDateFromTimestamp(timestamp: any, format = 'DD MMM yyyy HH:mm') {
  
  if (timestamp) {
    return moment(getDateFromTimestamp(timestamp))?.format(format);
  }

  return '';
}

export function getFutureRelativeTime(timestamp: any) {
  
  const relativeTime = moment().from(moment(getDateFromTimestamp(timestamp).getTime()));

  if (relativeTime?.startsWith("in")) {
    return "just now"
  }

  return relativeTime;
}

export function getPastRelativeTime(timestamp: any) {
  
  const relativeTime = moment(getDateFromTimestamp(timestamp).getTime()).from(moment());

  if (relativeTime?.startsWith("in")) {
    return "just now"
  }

  return relativeTime;
}

export function trimLeadingZeros(str: string | null | undefined) {
  
  if (!str) {
    return str;
  }

  return str.replace(/^0+/, '');
}

export async function adminUnlock(handleLockCommandsUrl: string, accessToken: string, deviceId: string) {

  if (!deviceId) {
    return;
  }

  if (!await debounceByKey({ key: `adminUnlock/${deviceId}`, duration: 5000 }).then(() => true).catch(() => false)) {
    return;
  }

  const response = await axios.post(
    handleLockCommandsUrl,
    JSON.stringify({ deviceId: deviceId, reqLockState: false, fromWeb: true, lockType: 'admin', lockCode: "-" }),
    { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' } }
  );

  if (response?.status !== 200) {
    throw new Error(response.data);
  }
}

export async function reApplyConfiguration(reapplyConfigurationUrl: string, accessToken: string, deviceId: string) {

  if (!deviceId) {
    return;
  }

  if (!await debounceByKey({ key: `reApplyConfiguration/${deviceId}`, duration: 5000 }).then(() => true).catch(() => false)) {
    return;
  }

  const response = await axios.post(
    `${reapplyConfigurationUrl}?deviceId=${deviceId}`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' } }
  );

  if (response?.status !== 200) {
    throw new Error(response.data);
  }
}

export async function updateAutoLock(updateDeviceAutoLockUrl: string, accessToken: string, deviceId: string, autoLockDelaySeconds: number) {
  
  if (!deviceId) {
    return;
  }

  if (!await debounceByKey({ key: `updateAutoLock/${deviceId}/${autoLockDelaySeconds}`, duration: 5000 }).then(() => true).catch(() => false)) {
    return;
  }
  
  const response = await axios.post(
    updateDeviceAutoLockUrl,
    JSON.stringify({ deviceId: deviceId, delaySeconds: autoLockDelaySeconds, lockCode: "-" }),
    { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }, timeout: 120000 },
  );

  if (response?.status !== 200) {
    throw new Error(response.data);
  }
}

export async function requestDeviceStatus(statusRequestCommandUrl: string, accessToken: string, deviceId: string, handleLockCommandsUrl?: string | null | undefined) {
  
  if (!deviceId) {
    return;
  }
  
  if (handleLockCommandsUrl) {
    await adminUnlock(handleLockCommandsUrl, accessToken, deviceId);
  }

  if (!await debounceByKey({ key: `requestDeviceStatus/${deviceId}`, duration: 5000 }).then(() => true).catch(() => false)) {
    return;
  }

  const response = await axios.post(
    statusRequestCommandUrl,
    JSON.stringify({ deviceId: deviceId, fromWeb: true }),
    { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' } }
  );
  
  if (response?.status !== 200) {
    throw new Error(response.data);
  }
}

export async function delaySeconds(seconds: number) {
  await new Promise(resolve => setTimeout(resolve, seconds * 1000));
}

export function processDeviceEvents(step: InstallationStep, installation: Installation) {
  
  if (!installation.deviceId) {
    return false;
  }

  if (step.dateVerified || step.dateFailed || installation.dateCompleted || installation.dateFailed || installation.id) {
    return false;
  }

  return true;
}

export function useRefreshComponent() {
    const [, setTick] = useState(0);
    return useCallback(() => setTick(t => t + 1), []);
}

export function isValidDeviceId(value: string): boolean {
    
    if (!value) {
        return false;
    }

    const trimmed = value.trim();

    // 10 digits total
    
    if (!/^\d{10}$/.test(trimmed)) {
        return false;
    }

    // first digit must NOT be 5 or 6
    
    const firstDigit = trimmed[0];
    return firstDigit !== "5" && firstDigit !== "6";
}

export function isValidRemoteId(value: string): boolean {
    
    if (!value) {
        return false;
    }

    const trimmed = value.trim();

    // 10 digits total

    if (!/^\d{10}$/.test(trimmed)) {
        return false;
    }

    const firstDigit = trimmed[0];

    // panic button: first digit 6
    // normal remote: first digit 5

    return firstDigit === "5" || firstDigit === "6";
}

export async function resetDeviceAsync(handleResetCommandsUrl: string, accessToken: string, deviceId: string, resetType: string, safe: boolean, deliveryMethod: string) {

    safe = !!safe;

    if (!deviceId || !resetType || !handleResetCommandsUrl || !accessToken || !deliveryMethod) {
        return;
    }

    if (!await debounceByKey({ key: `resetDeviceAsync/${deviceId}/${resetType}/${safe}/${deliveryMethod}`, duration: 5000 }).then(() => true).catch(() => false)) {
        return;
    }

    resetType = resetType
        .trim()
        .toLowerCase()
        .replaceAll('+', '-')
        .replaceAll(' ', '-')
        .replaceAll('--', '-')
        .replaceAll('--', '-')
        .replaceAll('--', '-')
        .replaceAll('--', '-')
        .trim();

    const response = await axios.post(
        handleResetCommandsUrl,
        JSON.stringify({ deviceId: deviceId, resetType: resetType, safe: safe, deliveryMethod: deliveryMethod }),
        { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' } }
    );

    if (response?.status !== 200) {
        throw new Error(response.data);
    }
}